import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Mail, CheckCircle, XCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Badge } from '@/components/ui/badge';
import { closeContactRequestSchema } from '@tyto/shared';
import { PageTopNav } from '@/components/nav/PageTopNav';
import { useParams } from 'react-router';

// Define the contact request type based on the database schema
interface ContactRequest {
  id: string;
  orgId: string;
  firstName: string;
  lastName: string;
  email: string;
  message: string;
  open: boolean;
  closeMessage: string | null;
  closedAt: string | null;
  closedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

type CloseContactValues = z.infer<typeof closeContactRequestSchema>;

const OrgMessages: React.FC = () => {
  const queryClient = useQueryClient();
  const { orgId } = useParams<{ orgId: string }>();
  const [selectedRequest, setSelectedRequest] = useState<ContactRequest | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Fetch contact requests from the organization
  const { data: contactRequests, isLoading, error } = useQuery({
    queryKey: ['organizationContactRequests'],
    queryFn: async () => {
      const response = await fetch(`/api/org/${orgId}/contactRequests`);
      if (!response.ok) {
        throw new Error('Failed to fetch contact requests');
      }
      return response.json() as Promise<ContactRequest[]>;
    },
  });

  // Setup form for closing contact requests
  const form = useForm<CloseContactValues>({
    resolver: zodResolver(closeContactRequestSchema),
    defaultValues: {
      closeMessage: '',
    },
  });

  // Mutation for closing a contact request
  const closeContactMutation = useMutation({
    mutationFn: async ({ messageId, closeMessage }: { messageId: string; closeMessage: string }) => {
      const response = await fetch(`/api/org/${orgId}/contactRequest/${messageId}/close`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ closeMessage }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to close contact request');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationContactRequests'] });
      setIsDialogOpen(false);
      form.reset();
    },
  });

  // Handle form submission
  const onSubmit = (data: CloseContactValues) => {
    if (selectedRequest) {
      closeContactMutation.mutate({
        messageId: selectedRequest.id,
        closeMessage: data.closeMessage,
      });
    }
  };

  // Handle opening the dialog for a specific request
  const handleOpenDialog = (request: ContactRequest) => {
    setSelectedRequest(request);
    setIsDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading contact requests...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-destructive p-4">
        <XCircle className="h-8 w-8 mx-auto mb-2" />
        <p>Error loading contact requests. Please try again later.</p>
      </div>
    );
  }

  const openRequests = contactRequests?.filter(req => req.open) || [];
  const closedRequests = contactRequests?.filter(req => !req.open) || [];

  return (
    <div className="container mx-auto">
      <PageTopNav pageName="Open Messages" />
      <p className="mb-4">
        Messages from people who have contacted your organization
      </p>


      {openRequests.length > 0 ? (
        <div className="space-y-4">
          {openRequests.map((request) => (
            <div key={request.id} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-medium text-lg">
                    {request.firstName} {request.lastName}
                  </h3>
                  <p className="text-sm text-muted-foreground">{request.email}</p>
                </div>
                <Badge variant="outline" className="ml-2">
                  {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
                </Badge>
              </div>
              <p className="my-2 whitespace-pre-wrap">{request.message}</p>
              <div className="flex justify-end mt-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleOpenDialog(request)}
                >
                  Close Message
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-muted-foreground py-8">
          No open messages at this time.
        </p>
      )}


      {/* Closed Contact Requests */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <CheckCircle className="mr-2 h-5 w-5" />
            Closed Messages
          </CardTitle>
          <CardDescription>
            Previously handled messages
          </CardDescription>
        </CardHeader>
        <CardContent>
          {closedRequests.length > 0 ? (
            <div className="space-y-4">
              {closedRequests.map((request) => (
                <div key={request.id} className="border rounded-lg p-4 bg-muted/30">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium text-lg">
                        {request.firstName} {request.lastName}
                      </h3>
                      <p className="text-sm text-muted-foreground">{request.email}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="mb-1">
                        {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
                      </Badge>
                      {request.closedAt && (
                        <p className="text-xs text-muted-foreground">
                          Closed {formatDistanceToNow(new Date(request.closedAt), { addSuffix: true })}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="border-l-2 pl-3 my-2">
                    <p className="text-sm font-medium">Original Message:</p>
                    <p className="text-sm whitespace-pre-wrap">{request.message}</p>
                  </div>
                  {request.closeMessage && (
                    <div className="border-l-2 border-primary pl-3 mt-4">
                      <p className="text-sm font-medium">Closing Note:</p>
                      <p className="text-sm whitespace-pre-wrap">{request.closeMessage}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No closed messages yet.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Dialog for responding to a contact request */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Close Message</DialogTitle>
            <DialogDescription>
              Provide a message to close this message. This will be recorded with the contact request.
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <>
              <div className="border rounded-md p-3 bg-muted/30 mb-4">
                <p className="text-sm font-medium mb-1">From: {selectedRequest.firstName} {selectedRequest.lastName}</p>
                <p className="text-sm text-muted-foreground mb-2">{selectedRequest.email}</p>
                <p className="text-sm whitespace-pre-wrap">{selectedRequest.message}</p>
              </div>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="closeMessage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Closing Note</FormLabel>
                        <FormDescription>
                          Provide a short note to close this message. This will not be sent to the person who sent the message. Respond to the message via email if necessary.
                        </FormDescription>
                        <FormControl>
                          <Textarea
                            placeholder="Enter your closing note..."
                            className="min-h-[120px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <DialogFooter className="flex justify-between gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={closeContactMutation.isPending}
                    >
                      {closeContactMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Closing...
                        </>
                      ) : (
                        'Close Message'
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { OrgMessages };
